import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useParams, useNavigate } from 'react-router-dom';
import './App.css';

// Utility function to create URL slugs
function createSlug(title, type) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 4)
    .join('-');
  return `/${type === 'podcast' ? 'podcast' : 'analysis'}/${slug}`;
}

function formatDate(dateString) {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
}

function Home() {
  const [allContent, setAllContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [contentFilter, setContentFilter] = useState('all'); // 'all', 'podcast', 'article'
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'mariners', 'prospects', etc.
  const itemsPerPage = 12;

  // Detect categories from content
  const detectCategories = (item) => {
    const text = `${item.title} ${item.description}`.toLowerCase();
    const categories = [];
    
    if (text.includes('mariner')) categories.push('mariners');
    if (text.includes('prospect') || text.includes('minor league')) categories.push('prospects');
    if (text.includes('trade') || text.includes('traded')) categories.push('trades');
    if (text.includes('free agent') || text.includes('free agency') || text.includes('signing')) categories.push('free-agency');
    if (text.includes('draft') || text.includes('drafted')) categories.push('draft');
    if (text.includes('ranking') || text.includes('top ') || text.includes('list')) categories.push('rankings');
    if (text.includes('scout') || text.includes('scouting')) categories.push('scouting');
    if (text.includes('mlb') || text.includes('major league')) categories.push('mlb-news');
    
    return categories.length > 0 ? categories : ['uncategorized'];
  };

  useEffect(() => {
    loadContent();
  }, []);

  useEffect(() => {
    // Filter content based on search term, content type, and category
    let filtered = allContent;
    
    // Filter by content type
    if (contentFilter === 'podcast') {
      filtered = filtered.filter(item => item.type === 'podcast');
    } else if (contentFilter === 'article') {
      filtered = filtered.filter(item => item.type === 'article');
    }
    
    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => {
        const categories = detectCategories(item);
        return categories.includes(categoryFilter);
      });
    }
    
    // Then filter by search term
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredContent(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchTerm, contentFilter, categoryFilter, allContent]);

  const loadContent = async () => {
    setLoading(true);
    
    // Check for cached data
    const cacheKey = 'rss_cache';
    const cacheTimeKey = 'rss_cache_time';
    const cacheExpiry = 15 * 60 * 1000; // 15 minutes in milliseconds
    
    const cachedData = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(cacheTimeKey);
    
    if (cachedData && cacheTime) {
      const age = Date.now() - parseInt(cacheTime);
      if (age < cacheExpiry) {
        console.log('✅ Using cached RSS data (age:', Math.round(age/1000), 'seconds)');
        const combined = JSON.parse(cachedData);
        setAllContent(combined);
        setFilteredContent(combined);
        setLoading(false);
        return;
      }
    }
    
    console.log('⏳ Fetching fresh RSS data...');
    
    // Load preview clips
    let previews = {};
    try {
      const previewRes = await fetch('/previews.json');
      if (previewRes.ok) {
        previews = await previewRes.json();
        console.log('✅ Loaded preview clips:', Object.keys(previews).length);
      }
    } catch (e) {
      console.log('No previews.json found - episodes will link to Patreon');
    }

    // Load both podcast and articles
    const [podcastItems, articleItems] = await Promise.all([
      fetchPodcast(previews),
      fetchArticles()
    ]);

    // Combine and sort by date
    const combined = [...podcastItems, ...articleItems].sort(
      (a, b) => new Date(b.pubDate) - new Date(a.pubDate)
    );

    // Cache the data
    localStorage.setItem(cacheKey, JSON.stringify(combined));
    localStorage.setItem(cacheTimeKey, Date.now().toString());
    localStorage.setItem('allContent', JSON.stringify(combined));
    
    setAllContent(combined);
    setFilteredContent(combined);
    setLoading(false);
  };

  const fetchPodcast = async (previews = {}) => {
    const proxies = [
      'https://api.allorigins.win/raw?url=',
      'https://corsproxy.io/?',
      'https://api.codetabs.com/v1/proxy?quest='
    ];

    for (const proxy of proxies) {
      try {
        const url = proxy + encodeURIComponent('https://www.patreon.com/rss/baseballthings?auth=H4LwlciFlSGkWzLCKP5eBUTTXPmIRoXp&show=866045');
        const response = await fetch(url);
        
        if (!response.ok) continue;
        
        const text = await response.text();
        if (!text.includes('<?xml')) continue;
        
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'text/xml');
        const items = xml.querySelectorAll('item');
        
        if (items.length === 0) continue;

        const channelImage = xml.querySelector('channel itunes\\:image')?.getAttribute('href') || '/logo.png';
        
        return Array.from(items).map((item, index) => {
          const link = item.querySelector('link')?.textContent || '';
          const guid = item.querySelector('guid')?.textContent || `ep-${index}`;
          
          let audioUrl = null;
          if (previews[guid]) {
            audioUrl = previews[guid].preview_url;
            console.log('✅ Using preview clip for:', item.querySelector('title')?.textContent);
          } else {
            console.log('⚠️  No preview for:', item.querySelector('title')?.textContent);
          }

          const title = item.querySelector('title')?.textContent || 'Untitled';
          let episodeImage = item.querySelector('itunes\\:image')?.getAttribute('href');
          
          if (!episodeImage) {
            const itunesNS = item.getElementsByTagNameNS('http://www.itunes.com/dtds/podcast-1.0.dtd', 'image');
            if (itunesNS && itunesNS.length > 0) {
              episodeImage = itunesNS[0].getAttribute('href');
              console.log('✅ Method 2 worked for:', title);
            }
          }
          
          if (!episodeImage) {
            for (const img of item.querySelectorAll('image')) {
              const href = img.getAttribute('href');
              if (href && href.includes('patreon')) {
                episodeImage = href;
                console.log('✅ Method 3 worked for:', title);
                break;
              }
            }
          }
          
          if (!episodeImage) {
            console.log('❌ No episode image found for:', title);
            episodeImage = channelImage;
          }

          return {
            type: 'podcast',
            title,
            description: item.querySelector('description')?.textContent || '',
            link,
            pubDate: item.querySelector('pubDate')?.textContent || '',
            image: episodeImage,
            guid,
            audioUrl,
            hasPreview: !!audioUrl
          };
        });
      } catch (error) {
        continue;
      }
    }
    return [];
  };

  const fetchArticles = async () => {
    const proxies = [
      'https://corsproxy.io/?',
      'https://api.allorigins.win/get?url=',
      'https://api.codetabs.com/v1/proxy?quest=',
      ''
    ];
    
    const feedUrls = [
      'https://midnightmariners.com/feed',
      'https://www.midnightmariners.com/feed',
      'https://midnightmariners.substack.com/feed'
    ];

    for (const feedUrl of feedUrls) {
      for (const proxy of proxies) {
        try {
          let url, response, text;
          
          if (proxy === 'https://api.allorigins.win/get?url=') {
            url = proxy + encodeURIComponent(feedUrl);
            response = await fetch(url);
            if (!response.ok) continue;
            const json = await response.json();
            if (!json.contents) continue;
            const parser = new DOMParser();
            const xml = parser.parseFromString(json.contents, 'text/xml');
            const items = xml.querySelectorAll('item');
            if (items.length > 0) {
              return parseArticleItems(items);
            }
          } else {
            url = proxy + (proxy ? encodeURIComponent(feedUrl) : feedUrl);
            response = await fetch(url, {
              headers: {
                'Accept': 'application/rss+xml, application/xml, text/xml, */*'
              }
            });
            if (!response.ok) continue;
            text = await response.text();
            if (!text || !text.includes('<?xml')) continue;
            const parser = new DOMParser();
            const xml = parser.parseFromString(text, 'text/xml');
            const items = xml.querySelectorAll('item');
            if (items.length === 0) continue;
            return parseArticleItems(items);
          }
        } catch (error) {
          continue;
        }
      }
    }
    return [];
  };

  const parseArticleItems = (items) => {
    return Array.from(items).map((item, index) => {
      let image = '/logo.png';
      
      // Try multiple methods to extract image
      for (const element of item.querySelectorAll('content, media\\:content, thumbnail, media\\:thumbnail')) {
        const url = element.getAttribute('url') || element.getAttribute('href');
        if (url && url.includes('http')) {
          image = url;
          break;
        }
      }

      const encoded = item.querySelector('encoded, content\\:encoded')?.textContent || '';
      const description = item.querySelector('description')?.textContent || '';

      // Extract image from HTML content
      if (image === '/logo.png' && encoded) {
        const imgMatch = encoded.match(/<img[^>]+src=["']([^"']+)["']/);
        if (imgMatch && imgMatch[1].includes('http')) {
          image = imgMatch[1];
        }
      }

      if (image === '/logo.png' && description) {
        const imgMatch = description.match(/<img[^>]+src=["']([^"']+)["']/);
        if (imgMatch && imgMatch[1].includes('http')) {
          image = imgMatch[1];
        }
      }

      // Strip paywall content for preview text
      let previewText = '';
      if (encoded) {
        // Parse HTML first
        const temp = document.createElement('div');
        temp.innerHTML = encoded;
        
        // Remove paywall elements
        temp.querySelectorAll('[data-component-name="PaywallToDOM"]').forEach(el => el.remove());
        temp.querySelectorAll('[class*="paywall"]').forEach(el => el.remove());
        temp.querySelectorAll('.subscription-widget-wrap').forEach(el => el.remove());
        
        // Remove "Read more" links
        temp.querySelectorAll('a').forEach(el => {
          if (/read\s+more/i.test(el.textContent)) {
            el.remove();
          }
        });
        
        // Remove scripts, styles, images, and other dangerous/unwanted elements
        temp.querySelectorAll('script, style, iframe, object, embed, img, figure').forEach(el => el.remove());
        
        // Remove captioned-image-container divs
        temp.querySelectorAll('.captioned-image-container').forEach(el => el.remove());
        
        // Remove empty divs and empty paragraphs
        temp.querySelectorAll('div, p').forEach(el => {
          if (!el.textContent.trim()) {
            el.remove();
          }
        });
        
        previewText = temp.innerHTML.trim();
      }

      return {
        type: 'article',
        title: item.querySelector('title')?.textContent || 'Untitled Article',
        description,
        previewText,
        link: item.querySelector('link')?.textContent || '',
        pubDate: item.querySelector('pubDate')?.textContent || '',
        image,
        guid: item.querySelector('guid')?.textContent || `article-${index}`
      };
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredContent.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredContent.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      <header className="header" onClick={() => window.location.hash = '/'} style={{ cursor: 'pointer' }}>
        <div className="header-content">
          <a href="https://www.patreon.com/baseballthings" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
            <img src="/logo.png" alt="BASEBALL THINGS" className="header-logo" />
          </a>
          <div className="title-container">
            <div className="author-name">Jason A. Churchill's</div>
            <h1 className="site-title">BASEBALL THINGS</h1>
          </div>
          <a href="https://www.midnightmariners.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
            <img src="/logo.png" alt="Baseball Things" className="header-logo-right" />
          </a>
        </div>
      </header>

      <main className="main-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading content...</p>
          </div>
        ) : (
          <section className="content-section">
            <div className="section-header">
              <div className="social-links-container">
                <a href="https://x.com/prospectinsider" target="_blank" rel="noopener noreferrer" className="social-link social-x" title="Follow on X">
                  <svg viewBox="0 0 24 24" className="social-icon" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a href="https://bsky.app/profile/prospectinsider.bsky.social" target="_blank" rel="noopener noreferrer" className="social-link social-bluesky" title="Follow on Bluesky">
                  <svg viewBox="0 0 24 24" className="social-icon" fill="currentColor">
                    <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
                  </svg>
                </a>
                <a href="https://www.facebook.com/baseballthingspod/" target="_blank" rel="noopener noreferrer" className="social-link social-facebook" title="Follow on Facebook">
                  <svg viewBox="0 0 24 24" className="social-icon" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@jason.churchill" target="_blank" rel="noopener noreferrer" className="social-link social-tiktok" title="Follow on TikTok">
                  <svg viewBox="0 0 24 24" className="social-icon" fill="currentColor">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                  </svg>
                </a>
                <a href="https://www.youtube.com/@baseballthings" target="_blank" rel="noopener noreferrer" className="social-link social-youtube" title="Subscribe on YouTube">
                  <svg viewBox="0 0 24 24" className="social-icon" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
                <a href="mailto:churchill@prospectinsider.com" className="social-link social-email" title="Email Churchill">
                  <svg viewBox="0 0 24 24" className="social-icon" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                  </svg>
                </a>
              </div>
              
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              
              <div className="header-links">
                <button
                  onClick={() => setContentFilter('all')}
                  className={`view-all-link ${contentFilter === 'all' ? 'active' : ''}`}
                >
                  Show All
                </button>
                <span className="link-separator">•</span>
                <button
                  onClick={() => setContentFilter('podcast')}
                  className={`view-all-link ${contentFilter === 'podcast' ? 'active' : ''}`}
                >
                  All Podcasts
                </button>
                <span className="link-separator">•</span>
                <button
                  onClick={() => setContentFilter('article')}
                  className={`view-all-link ${contentFilter === 'article' ? 'active' : ''}`}
                >
                  All Analysis
                </button>
              </div>
              
              {/* Category filters */}
              <div className="category-filters">
                <button
                  onClick={() => setCategoryFilter(categoryFilter === 'prospects' ? 'all' : 'prospects')}
                  className={`category-tag ${categoryFilter === 'prospects' ? 'active' : ''}`}
                >
                  Prospects
                </button>
                <button
                  onClick={() => setCategoryFilter(categoryFilter === 'trades' ? 'all' : 'trades')}
                  className={`category-tag ${categoryFilter === 'trades' ? 'active' : ''}`}
                >
                  Trades
                </button>
                <button
                  onClick={() => setCategoryFilter(categoryFilter === 'free-agency' ? 'all' : 'free-agency')}
                  className={`category-tag ${categoryFilter === 'free-agency' ? 'active' : ''}`}
                >
                  Free Agency
                </button>
                <button
                  onClick={() => setCategoryFilter(categoryFilter === 'draft' ? 'all' : 'draft')}
                  className={`category-tag ${categoryFilter === 'draft' ? 'active' : ''}`}
                >
                  Draft
                </button>
                <button
                  onClick={() => setCategoryFilter(categoryFilter === 'rankings' ? 'all' : 'rankings')}
                  className={`category-tag ${categoryFilter === 'rankings' ? 'active' : ''}`}
                >
                  Rankings
                </button>
                <button
                  onClick={() => setCategoryFilter(categoryFilter === 'scouting' ? 'all' : 'scouting')}
                  className={`category-tag ${categoryFilter === 'scouting' ? 'active' : ''}`}
                >
                  Scouting
                </button>
              </div>
            </div>

            {currentItems.length === 0 ? (
              <p className="empty-state">No content found</p>
            ) : (
              <div className="content-grid">
                {currentItems.map((item) => (
                  <Link
                    key={item.guid}
                    to={createSlug(item.title, item.type)}
                    className="content-card"
                  >
                    <div className="card-image">
                      <img
                        src={item.image}
                        alt={item.title}
                        onError={(e) => { e.target.src = '/logo.png'; }}
                      />
                      <div className="card-type">
                        {item.type === 'podcast' ? '🎧 LISTEN' : '📊 ANALYSIS'}
                      </div>
                    </div>
                    <div className="card-content">
                      <h3>{item.title}</h3>
                      <div className="card-byline">
                        <a
                          href="https://bit.ly/m/churchill"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="card-author-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Jason A. Churchill
                        </a>
                        <div className="baseball-separator">
                          <img src="/baseball-icon.png" alt="" />
                          <img src="/baseball-icon.png" alt="" />
                          <img src="/baseball-icon.png" alt="" />
                        </div>
                        <p className="card-date">{formatDate(item.pubDate)}</p>
                      </div>
                      <p className="card-description">
                        {item.type === 'article' && item.previewText
                          ? (() => {
                              const temp = document.createElement('div');
                              temp.innerHTML = item.previewText;
                              return temp.textContent || temp.innerText || '';
                            })().substring(0, 150)
                          : (() => {
                              const temp = document.createElement('div');
                              temp.innerHTML = item.description;
                              return temp.textContent || temp.innerText || '';
                            })().substring(0, 150)
                        }…
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination-bottom">
                {(() => {
                  const pagesToShow = [];
                  const maxPages = Math.min(currentPage + 4, totalPages);
                  for (let i = currentPage; i <= maxPages; i++) {
                    pagesToShow.push(i);
                  }
                  return pagesToShow.map((pageNum, index) => (
                    <React.Fragment key={pageNum}>
                      {index > 0 && <span className="page-separator">|</span>}
                      <button
                        onClick={() => handlePageChange(pageNum)}
                        className={`page-number-link ${pageNum === currentPage ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    </React.Fragment>
                  ));
                })()}
              </div>
            )}
          </section>
        )}
      </main>

      <footer className="footer">
        <div className="footer-content">
          <img src="/logo.png" alt="BASEBALL THINGS" className="footer-logo" />
          <div className="footer-center">
            <div className="footer-text">
              <span className="footer-year">© 2025-26</span>
              <span className="footer-divider"> </span>
              <span className="footer-bt">BASEBALL THINGS</span>
              <span className="footer-divider">   |   </span>
              <span className="footer-mam">MARINERS AFTER MIDNIGHT</span>
            </div>
            <div className="footer-links">
              <a href="https://www.patreon.com/baseballthings" target="_blank" rel="noopener noreferrer">
                Patreon
              </a>
              <span className="link-separator">•</span>
              <a href="https://midnightmariners.com" target="_blank" rel="noopener noreferrer">
                Substack
              </a>
            </div>
          </div>
          <img src="/logo.png" alt="Baseball Things" className="footer-logo" />
        </div>
      </footer>
    </div>
  );
}

function PreviewPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    const allContent = JSON.parse(localStorage.getItem('allContent') || '[]');
    const found = allContent.find(item => createSlug(item.title, item.type) === window.location.pathname);
    setContent(found);
  }, [slug]);

  if (!content) {
    return (
      <div className="App">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const stripHtml = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  return (
    <div className="App">
      <header className="header" onClick={() => window.location.hash = '/'} style={{ cursor: 'pointer' }}>
        <div className="header-content">
          <a href="https://www.patreon.com/baseballthings" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
            <img src="/logo.png" alt="BASEBALL THINGS" className="header-logo" />
          </a>
          <div className="title-container">
            <div className="author-name">Jason A. Churchill's</div>
            <h1 className="site-title">BASEBALL THINGS</h1>
          </div>
          <a href="https://www.midnightmariners.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
            <img src="/logo.png" alt="Baseball Things" className="header-logo-right" />
          </a>
        </div>
      </header>

      <main className="main-content">
        <button onClick={() => navigate(-1)} className="back-button">
          <span className="back-arrow">◄</span> Home
        </button>

        <div className="preview-container">
          <div className="preview-header">
            <img 
              src={content.image} 
              alt={content.title} 
              className="preview-image" 
              onError={(e) => { e.target.src = '/logo.png'; }}
            />
            <div className="preview-meta">
              <div className="preview-type-badge">
                {content.type === 'podcast' ? '🎧 LISTEN' : '📊 ANALYSIS'}
              </div>
              <h1 className="preview-title">{content.title}</h1>
              <div className="preview-byline">
                <a
                  href="https://bit.ly/m/churchill"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="author-link"
                >
                  Jason A. Churchill
                </a>
                <span className="byline-separator">•</span>
                <p className="preview-date">{formatDate(content.pubDate)}</p>
              </div>
            </div>
          </div>

          <div className="preview-content">
            <div 
              className="preview-description"
              dangerouslySetInnerHTML={{
                __html: content.type === 'article' && content.previewText 
                  ? content.previewText 
                  : stripHtml(content.description)
              }}
            />
          </div>

          {content.type === 'podcast' && content.audioUrl && (
            <div className="audio-player-section">
              <h3>🎧 60-Second Preview</h3>
              <div className="audio-player">
                <audio ref={audioRef} src={content.audioUrl} />
                <button
                  onClick={() => {
                    if (audioRef.current) {
                      if (isPlaying) {
                        audioRef.current.pause();
                      } else {
                        audioRef.current.play();
                      }
                      setIsPlaying(!isPlaying);
                    }
                  }}
                  className="play-button-large"
                >
                  {isPlaying ? '⏸ Pause Preview' : '▶ Play Preview'}
                </button>
                <p className="preview-note">Random 60-second clip with fade in/out</p>
              </div>
            </div>
          )}

          {content.type === 'podcast' && !content.audioUrl && (
            <div className="audio-player-section">
              <h3>🎧 Audio Preview</h3>
              <div className="audio-player">
                <p className="no-preview-message">
                  Preview clip coming soon! Listen to the full episode on Patreon.
                </p>
              </div>
            </div>
          )}

          <div className="subscribe-cta">
            <h3>{content.type === 'podcast' ? 'Full Episode' : 'Full Story'}</h3>
            <p>
              {content.type === 'podcast'
                ? 'Subscribe to BASEBALL THINGS on Patreon to get full episodes and exclusive content.'
                : 'Subscribe to MARINERS AFTER MIDNIGHT on Substack to read full articles.'
              }
            </p>
            <a
              href={content.type === 'podcast' 
                ? 'https://www.patreon.com/join/baseballthings' 
                : 'https://www.midnightmariners.com/subscribe'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="subscribe-button"
            >
              Subscribe
            </a>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <img src="/logo.png" alt="BASEBALL THINGS" className="footer-logo" />
          <div className="footer-center">
            <div className="footer-text">
              <span className="footer-year">© 2025-26</span>
              <span className="footer-divider"> </span>
              <span className="footer-bt">BASEBALL THINGS</span>
              <span className="footer-divider">   |   </span>
              <span className="footer-mam">MARINERS AFTER MIDNIGHT</span>
            </div>
            <div className="footer-links">
              <a href="https://www.patreon.com/baseballthings" target="_blank" rel="noopener noreferrer">
                Patreon
              </a>
              <span className="link-separator">•</span>
              <a href="https://midnightmariners.com" target="_blank" rel="noopener noreferrer">
                Substack
              </a>
            </div>
          </div>
          <img src="/logo.png" alt="Baseball Things" className="footer-logo" />
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/podcast/:slug" element={<PreviewPage />} />
        <Route path="/analysis/:slug" element={<PreviewPage />} />
      </Routes>
    </Router>
  );
}

export default App;
